import React, { useState } from 'react'
import UpdateLessonModal from './UpdateLessonModal'
import DeleteConfirmationModal from './DeleteConfirmationModal'
import { deleteLesson } from '../services/lessonsApi'
import { toast } from '../utils/toast'
import { formatError } from '../utils/errorHandler'
import './LessonsGrid.css'

function LessonsGrid({ lessons, onUpdate }) {
  const [selectedLesson, setSelectedLesson] = useState(null)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [lessonToDelete, setLessonToDelete] = useState(null)
  const [deletingLessonKey, setDeletingLessonKey] = useState(null)
  const [copiedKey, setCopiedKey] = useState(null)

  const handleUpdateClick = (lesson) => {
    setSelectedLesson(lesson)
    setShowUpdateModal(true)
  }

  const handleUpdateModalClose = () => {
    setShowUpdateModal(false)
    setSelectedLesson(null)
  }

  const handleUpdateSuccess = () => {
    handleUpdateModalClose()
    if (onUpdate) {
      onUpdate()
    }
  }

  const handleDeleteClick = (lesson) => {
    setLessonToDelete(lesson)
    setShowDeleteModal(true)
  }

  const handleDeleteModalClose = () => {
    if (deletingLessonKey) return // Prevent closing while deleting
    setShowDeleteModal(false)
    setLessonToDelete(null)
  }

  const handleDeleteConfirm = async () => {
    if (!lessonToDelete) return
    
    setDeletingLessonKey(lessonToDelete.lessonKey)
    
    try {
      await deleteLesson(lessonToDelete.lessonKey, lessonToDelete.etag)
      toast.success(`Lesson "${lessonToDelete.lessonName}" deleted successfully`)
      
      setShowDeleteModal(false)
      setLessonToDelete(null)
      
      if (onUpdate) {
        onUpdate()
      }
    } catch (error) {
      const errorMessage = formatError(error)
      toast.error(errorMessage, 5000)
    } finally {
      setDeletingLessonKey(null)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleString()
    } catch {
      return dateString
    }
  }

  if (lessons.length === 0) {
    return (
      <div className="no-lessons">
        <div className="no-lessons-icon">📚</div>
        <h3>No lessons found</h3>
        <p>Get started by creating your first lesson</p>
      </div>
    )
  }

  return (
    <>
      <div className="lessons-list">
        {lessons.map((lesson) => (
          <div key={lesson.lessonKey} className="lesson-card">
            <div className="lesson-card-header">
              <h3
                className="lesson-card-name"
                title={lesson.lessonName}
                onClick={() => handleUpdateClick(lesson)}
              >
                {lesson.lessonName}
              </h3>
              <span className="lesson-card-key-row">
                <code className="lesson-card-key">{lesson.lessonKey}</code>
                <button
                  className="copy-key-btn"
                  title="Copy lesson key"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigator.clipboard.writeText(lesson.lessonKey)
                    setCopiedKey(lesson.lessonKey)
                    setTimeout(() => setCopiedKey(null), 1500)
                  }}
                >
                  {copiedKey === lesson.lessonKey ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                  )}
                </button>
              </span>
            </div>
            <div className="lesson-card-body">
              {lesson.isAbsorbCourse && (
                <div className="lesson-card-badge-row">
                  <span className="absorb-badge">Absorb</span>
                  {lesson.passingScore != null && (
                    <span className="passing-score-badge">{lesson.passingScore}% to pass</span>
                  )}
                </div>
              )}
              <div className="lesson-card-info">
                <div className="lesson-info-item">
                  <span className="lesson-info-label">File:</span>
                  <span className="lesson-info-value">{lesson.fileDisplayName}</span>
                </div>
                <div className="lesson-info-item">
                  <span className="lesson-info-label">Updated:</span>
                  <span className="lesson-info-value">{formatDate(lesson.updatedAt)}</span>
                </div>
                {lesson.updatedBy && (
                  <div className="lesson-info-item">
                    <span className="lesson-info-label">By:</span>
                    <span className="lesson-info-value">{lesson.updatedBy}</span>
                  </div>
                )}
              </div>
              <button
                className="delete-icon-btn"
                title="Delete lesson"
                onClick={() => handleDeleteClick(lesson)}
                disabled={deletingLessonKey === lesson.lessonKey}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      {showUpdateModal && selectedLesson && (
        <UpdateLessonModal
          lesson={selectedLesson}
          onClose={handleUpdateModalClose}
          onSuccess={handleUpdateSuccess}
        />
      )}

      {showDeleteModal && lessonToDelete && (
        <DeleteConfirmationModal
          lesson={lessonToDelete}
          onClose={handleDeleteModalClose}
          onConfirm={handleDeleteConfirm}
          isDeleting={deletingLessonKey === lessonToDelete.lessonKey}
        />
      )}
    </>
  )
}

export default LessonsGrid

